import { captureException } from '@sentry/react-router'
import { useEffect, type ReactElement } from 'react'
import {
	type ErrorResponse,
	isRouteErrorResponse,
	Link,
	useParams,
	useRouteError,
} from 'react-router'
import { Icon } from '#app/components/ui/icon.tsx'
import { getErrorMessage } from '#app/utils/misc.tsx'

type StatusHandler = (info: {
	error: ErrorResponse
	params: Record<string, string | undefined>
}) => ReactElement | null

function ErrorPageLayout({ children }: { children: React.ReactNode }) {
	return (
		<div
			className="flex min-h-screen items-center justify-center px-6 py-20 bg-cathedral-page"
		>
			<div className="mx-auto flex w-full max-w-lg flex-col items-center text-center">
				{children}
				<Link
					to="/"
					className="cathedral-heading text-cathedral-gold hover:text-cathedral-gold-light mt-10 inline-flex items-center gap-2 rounded-sm border border-cathedral-gold/30 px-6 py-3 text-xs tracking-[0.14em] uppercase transition-colors"
				>
					<Icon name="arrow-left" className="h-4 w-4" />
					Back to Home
				</Link>
			</div>
		</div>
	)
}

function DefaultStatusError({ error }: { error: ErrorResponse }) {
	return (
		<ErrorPageLayout>
			<p className="cathedral-heading text-cathedral-gold/60 text-[8rem] leading-none font-bold tracking-wide">
				{error.status}
			</p>
			<div className="mt-4 h-px w-24 bg-cathedral-bright/30" />
			<h1 className="cathedral-heading text-cathedral-dark mt-6 text-2xl font-semibold tracking-wide md:text-3xl">
				Something Went Wrong
			</h1>
			<p className="cathedral-body text-cathedral-brown mt-4 text-lg leading-relaxed">
				{typeof error.data === 'string'
					? error.data
					: 'An unexpected error occurred.'}
			</p>
		</ErrorPageLayout>
	)
}

function UnexpectedError({ error }: { error: unknown }) {
	return (
		<ErrorPageLayout>
			<div className="flex h-20 w-20 items-center justify-center rounded-full border border-cathedral-gold/20 bg-cathedral-bright/5">
				<Icon name="cross-1" className="text-cathedral-gold/70 h-8 w-8" />
			</div>
			<h1 className="cathedral-heading text-cathedral-dark mt-8 text-2xl font-semibold tracking-wide md:text-3xl">
				Unexpected Error
			</h1>
			<p className="cathedral-body text-cathedral-brown mt-4 text-lg leading-relaxed">
				We encountered an unexpected problem. Please try again or return home.
			</p>
			<pre className="cathedral-body text-cathedral-dark mt-6 max-w-full overflow-auto rounded-sm border border-cathedral-gold/10 bg-cathedral-surface px-4 py-3 text-left text-sm">
				{getErrorMessage(error)}
			</pre>
		</ErrorPageLayout>
	)
}

export function GeneralErrorBoundary({
	defaultStatusHandler = ({ error }) => <DefaultStatusError error={error} />,
	statusHandlers,
	unexpectedErrorHandler = (error) => <UnexpectedError error={error} />,
}: {
	defaultStatusHandler?: StatusHandler
	statusHandlers?: Record<number, StatusHandler>
	unexpectedErrorHandler?: (error: unknown) => ReactElement | null
}) {
	const error = useRouteError()
	const params = useParams()
	const isResponse = isRouteErrorResponse(error)

	if (typeof document !== 'undefined') {
		console.error(error)
	}

	useEffect(() => {
		if (isResponse) return

		captureException(error)
	}, [error, isResponse])

	return (
		<>
			{isResponse
				? (statusHandlers?.[error.status] ?? defaultStatusHandler)({
						error,
						params,
					})
				: unexpectedErrorHandler(error)}
		</>
	)
}
